import React, { useEffect, useRef } from 'react';
import MeetingContactAvatar from 'flow/components/meeting/shared/MeetingContactAvatar';
import MeetingTileAvatarLayer from 'flow/components/meeting/shared/MeetingTileAvatarLayer';
import useVideoFrameReady from 'flow/hooks/useVideoFrameReady';
import { AvatarSize } from 'flow/components/ui-components/ChatAvatar';
import { ChatUserKey, ExtensionKey } from 'flow/types/EntityKeys';

interface MeetingMemberAvatarProps {
    isLocalVideo?: boolean;
    /** A dial-in participant: a glyph rather than initials when unresolved. */
    isCaller?: boolean;
    hasVideo: boolean;
    name: string;
    chatUserKey?: ChatUserKey;
    extensionKey?: ExtensionKey;
    phoneNumber?: string;
    videoStream?: MediaStream | null;
    size?: AvatarSize;
}

const MeetingMemberAvatar: React.FC<MeetingMemberAvatarProps> = ({
    isLocalVideo,
    isCaller,
    hasVideo,
    name,
    chatUserKey,
    extensionKey,
    phoneNumber,
    videoStream,
    size,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { hasVisibleFrame, onLoadedData, resetVisibleFrame } =
        useVideoFrameReady();

    useEffect(() => {
        if (hasVideo) {
            setVideoStreamToRef(videoRef, videoStream);
        } else {
            setVideoStreamToRef(videoRef, null);
        }
        resetVisibleFrame();
    }, [videoStream, hasVideo, resetVisibleFrame]);

    const setVideoStreamToRef = (
        ref: React.RefObject<HTMLVideoElement>,
        videoStream: MediaStream | null,
    ) => {
        if (!ref?.current) return;

        try {
            ref.current.srcObject = videoStream;
        } catch (e) {
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cant_define_property_object_not_extensible
            // Non-extensible object,
            console.warn('Setting this.videoRef.srcObject caught error:', e);
        }
    };

    const avatar = (
        <MeetingContactAvatar
            chatUserKey={chatUserKey}
            extensionKey={extensionKey}
            phoneNumber={phoneNumber}
            displayName={name}
            size={size}
            isCaller={isCaller}
        />
    );

    // Only the self-view shows video here; remote tiles draw their own
    if (!isLocalVideo || !hasVideo || !videoStream) {
        return (
            <div className="flex h-full w-full items-center justify-center rounded">
                {avatar}
            </div>
        );
    }

    return (
        <>
            <video
                ref={videoRef}
                autoPlay={true}
                muted={true}
                onLoadedData={onLoadedData}
                className="h-full w-full scale-x-[-1] rounded-sm object-cover"
            />
            {!hasVisibleFrame && (
                <MeetingTileAvatarLayer>{avatar}</MeetingTileAvatarLayer>
            )}
        </>
    );
};

export default MeetingMemberAvatar;
